import React, { useEffect, useMemo } from "react";
import "./ProfileTab.scss";
import { Card } from "primereact/card";
import { PersonalInfoForm } from "./PersonalInfoForm";
import { BioEditor } from "./BioEditor";
import { ExpertiseSelector } from "./ExpertiseSelector";
import { InterestsSelector } from "./InterestsSelector";
import { ProfileProgress } from "./ProfileProgress";

import { ProfileFormData } from "@features/settings/model/types";
import { useUnit } from "effector-react";
import { $countriesTerms } from "@/core/store/terms";
import { userService } from "@features/settings/service/userService";
import { Button } from "primereact/button";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { $userStore, fetchUserProfileRequested } from "@/features/user/store";
import {
  $userProfile,
  $userProfileLoading,
  fetchUserProfile,
} from "@features/settings/store";
import { InputText } from "primereact/inputtext";

export const ProfileTab: React.FC = () => {
  const countriesTerms = useUnit($countriesTerms);
  const userProfile = useUnit($userProfile);
  const loading = useUnit($userProfileLoading);
  const fetchProfile = useUnit(fetchUserProfile);

  useEffect(() => {
    if (!userProfile && !loading) {
      fetchProfile();
    }
  }, [userProfile, loading, fetchProfile]);

  const user = useUnit($userStore);

  // console.log(user.basicInfo?.uid, "user");

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setError,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<ProfileFormData>({
    defaultValues: {
      firstName: "",
      lastName: "",
      jobTitle: "",
      country: 0,
      city: "",
      phoneNumber: "",
      bio: "",
      linkedin: "",
      profilePrivacy: false,
      proposalWriting: false,
      projectManagement: false,
      financialManagement: false,
      communicationDissemination: false,
      partnershipDevelopment: false,
      expertise: [],
      sectors: [],
      userPicture: "",
      userPictureUrl: "",
    },
  });

  // Watch firstName for ProfileProgress
  const firstName = watch("firstName");
  const navigate = useNavigate();
  // Memoize the form data from userProfile
  const defaultFormData = useMemo(() => {
    if (!userProfile) return null;

    // Safely extract user_picture data
    const userPictureData = userProfile.user_data.user_picture;
    let userPictureFid = "";
    let userPictureUrl = "";

    if (userPictureData && typeof userPictureData === "object") {
      // If it's an object with fid and file_url
      // console.log(userPictureData, "picture data");
      userPictureFid = userPictureData?.[0]?.fid?.toString() || "";
      userPictureUrl = userPictureData?.[0]?.url || "";
    } else if (typeof userPictureData === "string") {
      // If it's just a string (FID)
      userPictureFid = userPictureData;
    }

    return {
      firstName: userProfile.user_data.field_firstname || "",
      lastName: userProfile.user_data.field_lastname || "",
      jobTitle: userProfile.user_data.field_job_title || "",
      country: parseInt(userProfile.user_data.field_country?.[0] || "0"),
      city: userProfile.user_data.field_city || "",
      phoneNumber: userProfile.user_data.field_phone_number || "",
      bio: userProfile.user_data.field_description || "",
      linkedin: userProfile.user_data.field_linkedin || "",
      proposalWriting:
        userProfile.user_data.field_expertise?.includes("803") || false,
      projectManagement: false,
      financialManagement: false,
      communicationDissemination: false,
      partnershipDevelopment: false,
      expertise:
        userProfile.user_data.field_expertise?.map((id: string) =>
          parseInt(id),
        ) || [],
      sectors:
        userProfile.user_data.field_sectors?.map((id: string) =>
          parseInt(id),
        ) || [],
      userPicture: userPictureFid,
      userPictureUrl: userPictureUrl,
    };
  }, [userProfile]);

  // Reset form when userProfile loads
  useEffect(() => {
    if (defaultFormData) {
      reset(defaultFormData);
    }
  }, [defaultFormData, reset]);

  const onSubmit = async (data: ProfileFormData) => {
    try {
      // Scroll to top when save starts
      window.scrollTo({ top: 0, behavior: "smooth" });

      await userService.updateProfile(data);
      fetchUserProfileRequested();
      // console.log("Profile updated successfully");

      // Refetch the profile to get updated data
      await fetchProfile();
    } catch (error: any) {
      console.error("Failed to update profile:", error);
      setError("root", {
        type: "manual",
        message: error.message || "Failed to update profile",
      });
    }
  };

  const handleCancel = () => {
    reset(); // Resets to last saved values
    // console.log("Changes cancelled");
  };

  // Get validation errors for PersonalInfoForm
  const validationErrors = Object.keys(errors).filter((key) =>
    ["firstName", "lastName", "jobTitle"].includes(key),
  );

  const profileHeader = (
    <div>
      <h2 className="text-xl font-semibold m-0">Personal info</h2>
      <p className="text-gray-600 mt-2">
        Update your photo and personal details here.
      </p>
    </div>
  );

  return (
    <div className="settings-profile">
      <ProfileProgress
        firstName={firstName}
        progress={userProfile?.profile_percentage || 0}
      />
      <div className="profile-header-section">
        <Card className="profile-header-card mb-4" header={profileHeader} />
        <div className="form-actions">
          <div className="form-actions-container top-cta button-section">
            <div
              onClick={() => navigate(`/users/${user.basicInfo?.uid}`)}
              className="view-profile-cta"
            >
              View Profile
            </div>
          </div>
        </div>
      </div>

      <Card className="profile-content-card">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="profile-section">
            <div className="profile-main">
              <PersonalInfoForm
                control={control}
                errors={errors}
                validationErrors={validationErrors}
              />

              <Controller
                name="sectors"
                control={control}
                render={({ field }) => (
                  <InterestsSelector
                    selectedSectors={field.value}
                    onSectorsChange={field.onChange}
                  />
                )}
              />
              <ExpertiseSelector control={control} />
            </div>
            <Controller
              name="bio"
              control={control}
              render={({ field }) => (
                <BioEditor value={field.value} onChange={field.onChange} />
              )}
            />
            <div className="form-row">
              <div className="form-group">
                <label>Social profiles(optional)</label>
                <div className="social-inputs">
                  <div className="social-label">
                    https://www.linkedin.com/in/
                  </div>
                  <Controller
                    name="linkedin"
                    control={control}
                    render={({ field }) => (
                      <InputText
                        {...field}
                        placeholder="danchambers"
                        className="w-full"
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>

          {errors.root && (
            <div className="error-message p-3 mb-3 bg-red-50 text-red-600 border-round">
              {errors.root.message}
            </div>
          )}

          <div className="form-actions">
            <div className="form-actions-container footer-cta">
              <Button
                label="Cancel"
                severity="secondary"
                className="button-cta"
                outlined
                onClick={handleCancel}
                type="button"
                disabled={!isDirty}
              />
              <Button
                label="Save"
                className="button-cta"
                type="submit"
                loading={isSubmitting}
                disabled={!isDirty}
              />
            </div>
          </div>
        </form>
      </Card>
    </div>
  );
};
